import { Empty, Flex, Image, List, Spin, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { CONVERSATION_MEDIA_THUMBNAIL_SIZE_PX } from "../../../constants/AppConstants";
import useConversationAttachments from "../../../hooks/useConversationAttachments";
import { formatDate } from "../../../pages/utils/CommonVessels";
import HNotification from "../../custom/notification/HNotification";
import "./ConversationFiles.css";

const { Text } = Typography;

/**
 * Lists all attachments for the current conversation (inbox thread) using the dedicated attachments API.
 */
export default function ConversationFiles({ appProperties, contactId, integId, fromNumber, toNumber, leftServiceId }) {
  const { fetchAllConversationAttachments } = useConversationAttachments(appProperties);
  const [conversationAttachmentsList, setConversationAttachmentsList] = useState([]);
  const [isLoadingConversationAttachments, setIsLoadingConversationAttachments] = useState(false);
  const [conversationAttachmentsError, setConversationAttachmentsError] = useState(null);

  useEffect(() => {
    setConversationAttachmentsList([]);
    setConversationAttachmentsError(null);
  }, [contactId]);

  const loadAttachments = useCallback(async () => {
    if (!integId || !fromNumber || !toNumber || !leftServiceId || !contactId) {
      setConversationAttachmentsList([]);
      return;
    }
    setIsLoadingConversationAttachments(true);
    setConversationAttachmentsError(null);
    try {
      const list = await fetchAllConversationAttachments({
        integId,
        fromNumber,
        toNumber,
        leftServiceId,
        contactId,
      });
      setConversationAttachmentsList(list);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("ConversationFiles load error", err);
      }
      const messageText = err?.message || "Failed to load media";
      setConversationAttachmentsError(messageText);
      HNotification.notify({
        type: "error",
        message: "Error",
        description: messageText,
      });
    } finally {
      setIsLoadingConversationAttachments(false);
    }
  }, [contactId, fetchAllConversationAttachments, fromNumber, integId, leftServiceId, toNumber]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const hasNoAttachments = !conversationAttachmentsList || conversationAttachmentsList.length === 0;

  if (isLoadingConversationAttachments && hasNoAttachments) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spin />
      </div>
    );
  }

  if (conversationAttachmentsError && hasNoAttachments) {
    return (
      <div className="px-2 py-3">
        <Text type="danger" className="hs-fs-12">
          {conversationAttachmentsError}
        </Text>
      </div>
    );
  }

  if (hasNoAttachments) {
    return <Empty className="my-4" description="No media in this conversation" />;
  }

  return (
    <Flex vertical className="h-100 overflow-auto w-100 px-2 pb-3">
      <List
        dataSource={conversationAttachmentsList}
        renderItem={(attachment) => {
          const mimeType = attachment.type || "";
          const isImage = mimeType.startsWith("image/");
          const isPdf = mimeType === "application/pdf";
          const isAudio = mimeType.startsWith("audio/");
          const isVideo = mimeType.startsWith("video/");
          const displayName = attachment.name || "File";

          return (
            <List.Item
              className="px-0 flex-wrap"
              key={`${attachment.url}-${attachment.name ?? ""}-${attachment.id ?? ""}`}
            >
              <div className="d-flex w-100 gap-2 align-items-start">
                <div className="flex-shrink-0">
                  {isImage ? (
                    <Image
                      src={attachment.url}
                      alt=""
                      width={CONVERSATION_MEDIA_THUMBNAIL_SIZE_PX}
                      height={CONVERSATION_MEDIA_THUMBNAIL_SIZE_PX}
                      className="rounded conv-files-thumb"
                      preview={{ mask: false, toolbarRender: () => null }}
                    />
                  ) : (
                    <span
                      className={`staticIconsSprite ${isPdf ? "pdf-icon" : "doc-icon"} d-inline-block`}
                      aria-hidden
                    />
                  )}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <a
                    href={attachment?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hs-fs-14 hs-fw-600 text-decoration-none d-block text-truncate"
                  >
                    {displayName}
                  </a>
                  {attachment?.messageCreatedTime ? (
                    <Text type="secondary" className="hs-fs-12 d-block">
                      {formatDate(attachment?.messageCreatedTime)}
                    </Text>
                  ) : null}
                  {mimeType ? (
                    <Text type="secondary" className="hs-fs-11 d-block text-break">
                      {mimeType}
                    </Text>
                  ) : null}
                  {isAudio ? (
                    <audio src={attachment?.url} controls className="w-100 mt-1 conv-files-audio" />
                  ) : null}
                  {isVideo ? (
                    <video src={attachment?.url} controls className="w-100 mt-1 conv-files-video" />
                  ) : null}
                </div>
              </div>
            </List.Item>
          );
        }}
      />
    </Flex>
  );
}
